import React, { useState, useEffect, useCallback } from "react";
import { Chart } from "primereact/chart";
import { Dropdown } from "primereact/dropdown";
import localeText from "../../../../../locale";
import dashboardService from "../../../services/DashboardService";
import { formatDate } from "../../../../../utilities/formatDate";

const MONTH_NAMES_FALLBACK = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

const buildChartDatasetConfig = () => ({
  label: localeText("performanceChartLabel"),
  fill: true,
  pointRadius: 3,
  tension: 0,
  backgroundColor: function (context) {
    const chart = context.chart;
    const { ctx, chartArea } = chart || {};
    if (!ctx || !chartArea) return null;
    function getCSSVar(varName, fallback) {
      if (typeof window !== "undefined" && window.getComputedStyle) {
        return (
          getComputedStyle(document.documentElement).getPropertyValue(varName)?.trim() || fallback
        );
      }
      return fallback;
    }
    let themePrimary = getCSSVar("--theme-primary", "#46A748");
    function cssColorToRgba(c, alpha) {
      if (!c) return `rgba(70,167,72,${alpha})`;
      c = c.replace(/\s+/g, "");
      if (c.startsWith("#")) {
        if (c.length === 7) {
          const r = parseInt(c.substr(1, 2), 16);
          const g = parseInt(c.substr(3, 2), 16);
          const b = parseInt(c.substr(5, 2), 16);
          return `rgba(${r},${g},${b},${alpha})`;
        }
        if (c.length === 4) {
          const r = parseInt(c[1] + c[1], 16);
          const g = parseInt(c[2] + c[2], 16);
          const b = parseInt(c[3] + c[3], 16);
          return `rgba(${r},${g},${b},${alpha})`;
        }
      }
      if (c.startsWith("rgb(")) return c.replace(/^rgb\((.*)\)$/i, `rgba($1,${alpha})`);
      if (c.startsWith("rgba("))
        return c.replace(/rgba\(([^,]+),([^,]+),([^,]+),[^)]+\)/i, `rgba($1,$2,$3,${alpha})`);
      return `rgba(70,167,72,${alpha})`;
    }
    const colorTop = cssColorToRgba(themePrimary, 0.8);
    const colorBottom = cssColorToRgba(themePrimary, 0.02);
    const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
    gradient.addColorStop(0, colorBottom);
    gradient.addColorStop(1, colorTop);
    return gradient;
  },
});

const transformApiResultToChartData = (res, interval) => {
  const result = res?.result ?? [];
  const labelsArr = [];
  const dataArr = [];
  const monthNames =
    typeof localeText("monthNames") === "object" && Array.isArray(localeText("monthNames"))
      ? localeText("monthNames")
      : MONTH_NAMES_FALLBACK;

  if (interval === 30) {
    for (let i = 0; i <= 30; i++) {
      const dateNow = new Date(new Date().setUTCDate(new Date().getUTCDate() - i));
      labelsArr.unshift(formatDate(dateNow));
      let foundMatch = false;
      result.forEach((item) => {
        const dateStats = new Date(item[1]);
        if (formatDate(dateStats) === formatDate(dateNow) && !foundMatch) {
          dataArr.unshift(item[0]);
          foundMatch = true;
        }
      });
      if (!foundMatch) dataArr.unshift(0);
    }
  } else {
    const months = interval / 30;
    for (let i = new Date().getUTCMonth(); i >= new Date().getUTCMonth() - (months - 1); i--) {
      let month = i;
      let isLastYear = false;
      if (month < 0) {
        month = month + 12;
        isLastYear = true;
      }
      let usersInMonth = 0;
      labelsArr.unshift(
        `${monthNames[month]} ${isLastYear ? new Date().getFullYear() - 1 : new Date().getFullYear()}`,
      );
      result.forEach((item) => {
        const dateStats = new Date(item[1]);
        if (dateStats.getUTCMonth() === month) {
          usersInMonth += item[0];
        }
      });
      dataArr.unshift(usersInMonth);
    }
  }

  return {
    labels: labelsArr,
    datasets: [{ ...buildChartDatasetConfig(), data: dataArr }],
  };
};

const PerformanceChart = ({ constrained = false }) => {
  const [chartData, setChartData] = useState(null);
  const [interval, setInterval] = useState(360);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const intervalOptions = [
    { label: `30 ${localeText("days")}`, value: 30 },
    { label: `3 ${localeText("months")}`, value: 90 },
    { label: `6 ${localeText("months")}`, value: 180 },
    { label: `1 ${localeText("year")}`, value: 360 },
  ];

  const fetchChartData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await dashboardService.getStatisticsGraph(interval);
      setChartData(transformApiResultToChartData(res, interval));
    } catch (err) {
      setError(err?.message || localeText("performanceChartErrorLoadingData"));
      setChartData(null);
    } finally {
      setLoading(false);
    }
  }, [interval]);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  const chartOptions = {
    plugins: {
      legend: {
        display: false,
      },
    },
    responsive: true,
    maintainAspectRatio: !constrained,
    hover: {
      mode: "index",
    },
    scales: {
      x: {
        display: true,
        title: {
          display: false,
          text: localeText("date"),
        },
      },
      y: {
        display: true,
        title: {
          display: false,
          text: localeText("quantity"),
        },
        beginAtZero: true,
      },
    },
  };

  const cardStyle = {
    border: "1px solid var(--theme-border, #EBEBEB)",
    ...(constrained
      ? {
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxSizing: "border-box",
        }
      : null),
  };

  const header = (
    <div
      className={`flex lg:flex-row flex-column lg:align-items-center lg:justify-content-between align-items-start justify-content-start gap-2 ${constrained ? "flex-shrink-0" : ""} mb-3`}
    >
      <div className="m-0 text-md font-semibold" style={{ color: "var(--theme-text)" }}>
        <i className="pi pi-chart-line mr-2"></i>
        {localeText("performanceChartTitle")}
      </div>
      <Dropdown
        value={interval}
        options={intervalOptions}
        onChange={(e) => setInterval(e.value)}
        className="w-full md:w-14rem p-no-outline border-round-lg"
        style={{ backgroundColor: "var(--theme-background, #FFFFFF)" }}
      />
    </div>
  );

  if (loading && !chartData) {
    return (
      <div className="card border-round-xl" style={cardStyle}>
        {header}
        <div
          className="flex align-items-center justify-content-center py-6"
          style={{ color: "var(--theme-text-muted, #6c757d)", flex: constrained ? 1 : undefined }}
        >
          {localeText("performanceChartLoading")}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card border-round-xl" style={cardStyle}>
        {header}
        <div
          className="flex align-items-center justify-content-center py-6"
          style={{ color: "var(--theme-text-muted, #6c757d)", flex: constrained ? 1 : undefined }}
        >
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="card border-round-xl" style={cardStyle}>
      {header}

      {constrained ? (
        <div style={{ flex: 1, minHeight: 0, width: "100%", position: "relative" }}>
          <Chart
            type="line"
            data={chartData}
            options={chartOptions}
            style={{ width: "100%", height: "100%" }}
          />
        </div>
      ) : (
        <Chart type="line" data={chartData} options={chartOptions} />
      )}

      <div className={`mt-3 ${constrained ? "flex-shrink-0" : ""}`}>
        <a href="#" style={{ color: "var(--theme-primary)" }}>
          {localeText("accessCommercialDashboard")}
        </a>
      </div>
    </div>
  );
};

export default React.memo(PerformanceChart);
